import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";

import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { memo, useEffect, useMemo, useState } from "react";
import type { ECharts } from "echarts";
import {
  ChartCard,
  ChartEmptyState,
  MultiLineChart,
  createEChartsTheme,
  type MultiLineChartConfig,
  type MultiLineChartPin,
  type MultiLineChartThreshold,
} from "@/shared/charts";

import type {
  LiveViewQueue,
  QueueType,
} from "@/features/deployment-dashboard/live-view/model/queue";
import {
  inferThresholdPresentation,
  summarizeQueueWait,
} from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend/queue-wait-trend.utils";
import type { QueueWaitTrendData } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend/types";
import {
  AVERAGE_QUEUE_WAIT_SERIES_ID,
  toQueueWaitChartData,
} from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend/types";
import { formatQueueWait } from "@/features/deployment-dashboard/shared/queue/queue-wait";

interface QueueWaitTrendChartProps {
  xAxisRange: { min: number; max: number };
  queues?: readonly QueueWaitTrendData[];
  loading?: boolean;
  waitingForFilters?: boolean;
  error?: Error | null;
  onChartReady?: (instance: ECharts) => void;
  playbackTimestamp?: number;
  additionalPins?: readonly MultiLineChartPin[];
}

interface QueueSelectorProps {
  queues: readonly LiveViewQueue[];
  selectedQueueId: string | undefined;
  onQueueChange: (queueId: string) => void;
}

interface SummaryCardProps {
  label: string;
  value: string;
  valueColor?: string;
}

const queueTypeLabels: Record<QueueType, string> = {
  general: "General",
  priority: "Priority",
};

const queueStatusLabels = {
  green: "Short wait",
  amber: "Moderate wait",
  red: "Long wait",
} as const;

function SummaryCard({ label, value, valueColor }: SummaryCardProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        minWidth: { xs: 120, sm: 138 },
        flex: "1 1 138px",
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "medium", color: valueColor }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

function QueueSelector({ queues, selectedQueueId, onQueueChange }: QueueSelectorProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        borderRight: { xs: 0, md: 1 },
        borderBottom: { xs: 1, md: 0 },
        borderColor: "divider",
        pr: { xs: 0, md: 1.5 },
        pb: { xs: 1.5, md: 0 },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        Queue
      </Typography>
      <RadioGroup
        aria-label="Queue"
        value={selectedQueueId ?? ""}
        onChange={(_, value) => onQueueChange(value)}
        sx={{ overflowY: "auto", flex: "1 1 0" }}
      >
        {queues.map((queue) => (
          <FormControlLabel
            key={queue.queue_id}
            value={queue.queue_id}
            control={
              <Radio
                size="small"
                slotProps={{ input: { "aria-label": queue.queue_display_name } }}
              />
            }
            label={
              <Box sx={{ minWidth: 0, py: 0.25 }}>
                <Typography variant="body2" noWrap>
                  {queue.queue_display_name}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {queueTypeLabels[queue.queue_type]}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        queue.status === "green"
                          ? theme.palette.success.main
                          : queue.status === "amber"
                            ? theme.palette.warning.light
                            : queue.status === "red"
                              ? theme.palette.error.light
                              : theme.palette.text.disabled,
                    }}
                    noWrap
                  >
                    ·{" "}
                    {queue.status === null
                      ? queue.historicalData
                        ? "Historical"
                        : "Unavailable"
                      : queueStatusLabels[queue.status]}
                  </Typography>
                </Stack>
              </Box>
            }
            sx={{
              alignItems: "flex-start",
              mx: 0,
              mb: 0.25,
              "& .MuiFormControlLabel-label": { minWidth: 0 },
            }}
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

function formatWaitDuration(value: number | null): string {
  if (value == null) return "–";
  if (value > 0 && value < 1) return "<1 minute";
  return `${Math.round(Math.max(0, value))} min`;
}

export const QueueWaitTrendChart = memo(function QueueWaitTrendChart({
  xAxisRange,
  queues = [],
  loading = false,
  waitingForFilters = false,
  error = null,
  onChartReady,
  playbackTimestamp,
  additionalPins,
}: QueueWaitTrendChartProps) {
  const theme = useTheme();
  const trendData = queues;
  const queueItems = useMemo(() => trendData.map((data) => data.queue), [trendData]);
  const [selectedQueueId, setSelectedQueueId] = useState<string | undefined>(
    queues[0]?.queue.queue_id
  );
  const [showThresholds, setShowThresholds] = useState(true);

  useEffect(() => {
    if (selectedQueueId != null && queueItems.some((queue) => queue.queue_id === selectedQueueId))
      return;
    setSelectedQueueId(queueItems[0]?.queue_id);
  }, [queueItems, selectedQueueId]);

  const selectedTrendData = trendData.find((data) => data.queue.queue_id === selectedQueueId);
  const selectedQueue = selectedTrendData?.queue;
  const selectedChartData = useMemo(
    () => (selectedTrendData == null ? [] : toQueueWaitChartData(selectedTrendData)),
    [selectedTrendData]
  );

  const chartTheme = useMemo(() => createEChartsTheme(theme), [theme]);
  const thresholds = useMemo<MultiLineChartThreshold[]>(() => {
    const waitThresholds = (selectedTrendData?.thresholds ?? []).filter(
      (threshold) => threshold.metric === "estimated_queue_wait_time_minutes"
    );
    return waitThresholds.map((threshold) => {
      const presentation = inferThresholdPresentation(threshold, waitThresholds);
      return {
        value: threshold.value,
        label: `${presentation.label} · ${threshold.value} min`,
        severity: presentation.severity,
      };
    });
  }, [selectedTrendData]);

  const thresholdRanges = useMemo(
    () =>
      thresholds.map((threshold) => ({
        label: threshold.label,
        color: threshold.severity
          ? chartTheme.threshold[threshold.severity].referenceLineColor
          : chartTheme.axisLineColor,
      })),
    [chartTheme, thresholds]
  );

  const chartConfig = useMemo<MultiLineChartConfig | null>(() => {
    if (selectedQueue == null) return null;
    return {
      series: [
        {
          id: AVERAGE_QUEUE_WAIT_SERIES_ID,
          label: "Average Wait Time",
          type: "bar",
          thresholdColoring: "severity",
        },
      ],
      ariaDescription: `Average queue wait time for ${selectedQueue.queue_display_name} over time`,
      animation: false,
      xAxisRange,
      valueFormatter: formatWaitDuration,
      thresholds,
      thresholdsVisible: showThresholds,
    };
  }, [selectedQueue, showThresholds, thresholds, xAxisRange]);

  const summaries = useMemo(
    () => trendData.map((data) => ({ data, summary: summarizeQueueWait(data) })),
    [trendData]
  );
  const bestPerformingQueue = summaries.reduce<(typeof summaries)[number] | undefined>(
    (best, current) => {
      const currentAverage = current.summary.averageWaitMinutes;
      if (currentAverage == null) return best;
      return best == null ||
        best.summary.averageWaitMinutes == null ||
        currentAverage < best.summary.averageWaitMinutes
        ? current
        : best;
    },
    undefined
  );
  const worstPerformingQueue = summaries.reduce<(typeof summaries)[number] | undefined>(
    (worst, current) => {
      const currentAverage = current.summary.averageWaitMinutes;
      if (currentAverage == null) return worst;
      return worst == null ||
        worst.summary.averageWaitMinutes == null ||
        currentAverage > worst.summary.averageWaitMinutes
        ? current
        : worst;
    },
    undefined
  );
  const busiestQueue = queueItems.reduce<LiveViewQueue | undefined>(
    (busiest, queue) =>
      queue.peopleInQueue === null ||
      (busiest !== undefined &&
        (busiest.peopleInQueue === null || queue.peopleInQueue <= busiest.peopleInQueue))
        ? busiest
        : queue,
    undefined
  );
  const selectedSummary = selectedTrendData == null ? null : summarizeQueueWait(selectedTrendData);
  const hasRenderableData =
    selectedTrendData?.points.some(
      (point) => point.averageWaitMinutes != null || point.peakWaitMinutes != null
    ) ?? false;
  const hasAnyRenderableData = trendData.some((data) =>
    data.points.some((point) => point.averageWaitMinutes != null || point.peakWaitMinutes != null)
  );

  return (
    <ChartCard
      title="Average Queue Wait Time"
      icon={<AccessTimeOutlinedIcon sx={{ fontSize: "0.875rem" }} />}
      action={
        thresholds.length > 0 ? (
          <FormControlLabel
            label="Show thresholds"
            control={
              <Checkbox
                size="small"
                checked={showThresholds}
                onChange={(event) => setShowThresholds(event.target.checked)}
                slotProps={{ input: { "aria-label": "Show thresholds" } }}
              />
            }
            sx={{
              mr: 0,
              color: "text.secondary",
              "& .MuiFormControlLabel-label": { fontSize: 12 },
            }}
          />
        ) : undefined
      }
      footerNote="Estimated wait time is shown for the selected queue."
      contentSx={{ gap: 1.25 }}
    >
      {waitingForFilters ? (
        <ChartEmptyState
          title="Select playback filters"
          description="Choose an area and date range above, then select Apply to load playback data."
        />
      ) : loading ? (
        <ChartEmptyState
          title="Loading queue wait trends"
          description="Fetching queue observations…"
        />
      ) : error !== null ? (
        <ChartEmptyState
          title="Unable to load queue wait trends"
          description="Queue wait trend data could not be loaded."
        />
      ) : queues.length === 0 ? (
        <ChartEmptyState
          title="No queues configured"
          description="Queue wait trends will appear once queues are available for this deployment."
        />
      ) : !hasAnyRenderableData ? (
        <ChartEmptyState
          title="No queue wait trend data"
          description="Queue wait trend data will appear once observations are available for the selected period."
        />
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <SummaryCard
              label={`${selectedQueue?.queue_display_name ?? "Selected queue"} average`}
              value={formatQueueWait(
                selectedSummary?.averageWaitMinutes == null
                  ? null
                  : Math.round(selectedSummary.averageWaitMinutes),
                (selectedSummary?.averageWaitMinutes ?? 1) < 1 &&
                  (selectedTrendData?.points.some((point) => point.nearEmptyWait) ?? false)
              )}
              valueColor={theme.palette.secondary.main}
            />
            {queueItems.length > 1 && (
              <SummaryCard label="Busiest queue" value={busiestQueue?.queue_display_name ?? "–"} />
            )}
            {queueItems.length > 1 && (
              <>
                <SummaryCard
                  label="Best performing"
                  value={bestPerformingQueue?.data.queue.queue_display_name ?? "–"}
                />
                <SummaryCard
                  label="Worst performing"
                  value={worstPerformingQueue?.data.queue.queue_display_name ?? "–"}
                />
              </>
            )}
          </Box>

          {showThresholds && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: "wrap" }}
              useFlexGap
              aria-label="Queue wait threshold ranges"
            >
              {thresholdRanges.map((range) => (
                <Box key={range.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 18,
                      borderTop: "2px dotted",
                      borderColor: range.color,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {range.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              flex: "1 1 auto",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: 250 },
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                maxHeight: { xs: 260, md: "none" },
              }}
            >
              <QueueSelector
                queues={queueItems}
                selectedQueueId={selectedQueueId}
                onQueueChange={setSelectedQueueId}
              />
            </Box>

            <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 290 }}>
              {selectedQueue == null ||
              selectedTrendData == null ||
              chartConfig == null ||
              !hasRenderableData ? (
                <ChartEmptyState
                  title="No queue wait trend data"
                  description="Queue wait trend data will appear once observations are available."
                />
              ) : (
                <MultiLineChart
                  data={selectedChartData}
                  config={chartConfig}
                  height={290}
                  activeTimestamp={playbackTimestamp}
                  additionalPins={additionalPins}
                  onChartReady={onChartReady}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </ChartCard>
  );
});
